import {SITE_ROLES, type SiteRolesType} from './site';

// maximum custom site roles allowed per workspace
export const MAX_CUSTOM_SITE_ROLES = 20;

export const BaseRoleNames = {
  REVIEW: 'review',
  EDIT_CONTENT: 'editContent',
  BUILD_PAGES: 'buildPages',
  DESIGN: 'design',
} as const;

export type BaseRoleNameKeys = keyof typeof BaseRoleNames;
export type BaseRoleNameValues =
  (typeof BaseRoleNames)[keyof typeof BaseRoleNames];

type TierBaseRoleKeys =
  | 'maxReviewerSeats'
  | 'maxLimitedAccessSeats'
  | 'maxFullAccessSeats';

export const TIER_CUSTOM_BASE_ROLES: {
  [key in TierBaseRoleKeys]: Array<BaseRoleNameValues>;
} = {
  maxReviewerSeats: [BaseRoleNames.REVIEW],
  maxLimitedAccessSeats: [
    BaseRoleNames.EDIT_CONTENT,
    BaseRoleNames.BUILD_PAGES,
  ],
  maxFullAccessSeats: [BaseRoleNames.DESIGN],
};

export enum AdditionalPermissionNames {
  PUBLISH_TO_PRODUCTION = 'publishToProduction',
  PUBLISH_TO_STAGING = 'publishToStaging',
  SPP_PUBLISH_TO_PRODUCTION = 'sppPublishToProduction',
  SPP_PUBLISH_TO_STAGING = 'sppPublishToStaging',
  PUBLISH_CMS_TO_PRODUCTION = 'publishCmsToProduction',
  QUEUE_CMS_FOR_PUBLISH = 'queueCmsForPublish',
  APPROVE_CHANGES = 'approveChanges',
  CREATE_PAGES_WITHOUT_TEMPLATE = 'createPagesWithoutTemplate',
  MAKE_CHANGES = 'makeChangesWithoutApproval',
  MERGE_APPROVED = 'mergeApprovedBranch',
  EDIT_MAIN_SITE_WITHOUT_APPROVAL = 'editMainSiteWithoutApproval',
  EDIT_CMS_ITEMS = 'editCmsItems',
  MANAGE_COMPONENTS = 'manageComponents',
  MANAGE_CLASSES = 'manageClasses',
  MANAGE_VARIABLES = 'manageVariables',
  MANAGE_SITE_SETTINGS = 'manageSiteSettings',
  EDIT_COMPONENT_CONTENT = 'editComponentContent',
  USE_AI_FEATURES = 'useAIFeatures',
}

export type AdditionalPermission = AdditionalPermissionNames;

// additional permission categories
export enum UxPermissionGroupNames {
  PUBLISHING = 'Publishing',
  BUILD_AND_REVIEW = 'Build & review',
  CMS = 'CMS',
  AI_FEATURES = 'AI',
  SITE_MANAGEMENT = 'Site management',
  ADMIN = 'Admin',
}

export type UxPermissionGroup = UxPermissionGroupNames;

// additional permission subgroup categories
export enum UxPermissionSubGroupNames {
  FULL_SITE = 'Full site',
  INDIVIDUAL_PAGES = 'Individual pages',
  BRANCHES = 'Branches',
  DESIGN = 'Design capabilities',
}

export type UxPermissionSubGroup = UxPermissionSubGroupNames;

export const DEFAULT_ROLE_PERMISSIONS_ADD_ON: {
  [roleKey in SiteRolesType]?: {
    [groupName in UxPermissionGroup]?: Array<AdditionalPermission>;
  };
} = {
  [SITE_ROLES.page_builder_v2]: {
    [UxPermissionGroupNames.BUILD_AND_REVIEW]: [
      AdditionalPermissionNames.MAKE_CHANGES,
      AdditionalPermissionNames.EDIT_MAIN_SITE_WITHOUT_APPROVAL,
    ],
    [UxPermissionGroupNames.CMS]: [AdditionalPermissionNames.EDIT_CMS_ITEMS],
  },
};

type PermissionDescription = {
  [K in AdditionalPermission]: {
    explainer: string;
    label: string;
  };
};

export const PERMISSION_DESCRIPTIONS: PermissionDescription = {
  [AdditionalPermissionNames.PUBLISH_TO_PRODUCTION]: {
    explainer: 'Publish all changes to the live site',
    label: 'Publish site to production',
  },
  [AdditionalPermissionNames.PUBLISH_TO_STAGING]: {
    explainer: 'Publish all changes to a staging environment',
    label: 'Publish site to staging',
  },
  [AdditionalPermissionNames.SPP_PUBLISH_TO_PRODUCTION]: {
    explainer: 'Publish individual pages to the live site',
    label: 'Publish single page to production',
  },
  [AdditionalPermissionNames.SPP_PUBLISH_TO_STAGING]: {
    explainer: 'Publish individual pages to a staging environment',
    label: 'Publish single page to staging',
  },
  [AdditionalPermissionNames.PUBLISH_CMS_TO_PRODUCTION]: {
    explainer: 'Publish individual CMS items to the live site',
    label: 'Publish CMS items to production',
  },
  [AdditionalPermissionNames.QUEUE_CMS_FOR_PUBLISH]: {
    explainer: 'Queue individual CMS items for the next site publish',
    label: 'Queue CMS items for publish',
  },
  [AdditionalPermissionNames.APPROVE_CHANGES]: {
    explainer: 'Approve changes in branches to be merged',
    label: 'Approve changes',
  },
  [AdditionalPermissionNames.CREATE_PAGES_WITHOUT_TEMPLATE]: {
    explainer: 'Can build new pages without using a static page template',
    label: 'Create new pages without a template',
  },
  [AdditionalPermissionNames.MAKE_CHANGES]: {
    explainer:
      'Make changes to the main site and edit CMS items without requiring approval to merge branches',
    label: 'Edit site and CMS items without approval',
  },
  [AdditionalPermissionNames.MERGE_APPROVED]: {
    explainer: 'Merge branches into the main site once approved',
    label: 'Merge approved branches',
  },
  [AdditionalPermissionNames.EDIT_MAIN_SITE_WITHOUT_APPROVAL]: {
    explainer:
      'Change the main site directly instead of in a branch that needs review.',
    label: 'Edit main site directly',
  },
  [AdditionalPermissionNames.EDIT_CMS_ITEMS]: {
    explainer: 'Create, edit, and manage CMS and Ecommerce content',
    label: 'Edit CMS items',
  },
  [AdditionalPermissionNames.MANAGE_COMPONENTS]: {
    explainer: 'Create & modify components',
    label: 'Manage components',
  },
  [AdditionalPermissionNames.MANAGE_CLASSES]: {
    explainer: 'Can modify existing classes',
    label: 'Modify classes',
  },
  [AdditionalPermissionNames.MANAGE_VARIABLES]: {
    explainer: 'Create and modify variables',
    label: 'Manage variables',
  },
  [AdditionalPermissionNames.MANAGE_SITE_SETTINGS]: {
    explainer:
      'Can make changes to site settings pages like SEO, Custom Code, and Apps & Integrations in the dashboard. Note: Site access will still be visible',
    label: 'Manage site settings',
  },
  [AdditionalPermissionNames.EDIT_COMPONENT_CONTENT]: {
    explainer: 'Can make edits that apply to all component instances',
    label: 'Edit global component content',
  },
  [AdditionalPermissionNames.USE_AI_FEATURES]: {
    explainer: 'Use AI to make changes across the site within role limits',
    label: 'Use Webflow AI',
  },
};

export type PermissionData = {
  action: string;
  resource: ResourceKeys;
  isCheckedRule?: (arg0: boolean) => boolean;
};

type UxPermissionData = {
  action?: string;
  uxBaseRoleFilter?: (role: string) => boolean;
  group: UxPermissionGroup;
  subgroup?: UxPermissionSubGroup;
  resource?: ResourceKeys;
  ruleSet?: Array<PermissionData>;
  // Replaces ruleSet for the listed base roles so a checkbox can stay capped
  // by what each base role already allows. A base role that isn't listed
  // falls back to ruleSet, so offer the permission only to roles it covers
  // (uxBaseRoleFilter). An empty list also falls back to ruleSet, so it can't
  // be used to grant a role nothing.
  ruleSetByBaseRole?: Partial<
    Record<BaseRoleNameValues, Array<PermissionData>>
  >;
  requiredFeatureFlags?: string[];
  // Hides the permission when any listed flag is on. This only hides the
  // checkbox in the UI; it is not a server gate.
  hiddenByFeatureFlags?: string[];
  uxDependentsToSelect?: Array<{
    dependentPermissionName: AdditionalPermission;
  }>;
  uxDependentsToSelectByBaseRole?: Partial<
    Record<
      BaseRoleNameValues,
      Array<{
        dependentPermissionName: AdditionalPermission;
      }>
    >
  >;
  uxDependentsToDisable?: Array<{
    dependentPermissionName: AdditionalPermission;
    reason: string;
  }>;
};

export type UxPermission = [AdditionalPermission, UxPermissionData];

export type FeatureFlagsMap = Record<string, boolean>;

export type RequestPermissions = {
  [K in AdditionalPermission]?: boolean;
};

export type ResourceKeys =
  | 'site'
  | 'siteAI'
  | 'siteBilling'
  | 'siteBranch'
  | 'siteCollection'
  | 'siteCollectionItem'
  | 'siteCommerce'
  | 'siteCommerceItem'
  | 'siteCustomCode'
  | 'siteCustomDomain'
  | 'siteDesignerModes'
  | 'siteLibrary'
  | 'siteLocale'
  | 'sitePage'
  | 'siteRedirects'
  | 'siteSnapshot'
  | 'siteStyleBlock'
  | 'siteSymbol'
  | 'siteVariable';

export type PermissionParameters = {
  action: string;
  checked: boolean;
  resource: ResourceKeys;
  isCheckedRule?: (arg0: boolean) => boolean;
};

export type PermissionParameterList = Array<PermissionParameters>;

export interface ResourceSet {
  [key: string]: boolean;
}
